/**
 * /en/admin/structure/paragraphs_type/vuc_hours_of_operation/fields
 */

import CLASS_NAMES from "../constants/css-constants.es6.js";
import { DOM_EVENTS } from "../constants/html-constants.es6.js";
import { KEYS } from "../constants/js-constants.es6.js";
import Field from "../core/field.es6.js";
import Paragraph from "../core/paragraph.es6.js";
import HoursRowParagraphField from "../fields/hours-row-paragraph-field.es6.js";
import DateDisplayUtils from "@js/utils/date-display-utils.es6.js";

export default class VirtualUrgentCareHoursOfOperationParagraph extends Paragraph {
  #luminisServiceUtils;

  constructor({ luminisServiceUtils, parentElement }) {
    super({
      machineName: 'vuc_hours_of_operation',
      parentElement,
    });

    this.#luminisServiceUtils = luminisServiceUtils;
  }

  get currentlyClosedHeader() {
    return new Field({
      machineName: 'field_h4',
      parentElement: this,
    });
  }

  get hideHoursToggle() {
    return new Field({
      machineName: 'field_hide_hours_toggle',
      parentElement: this,
    });
  }

  get hoursRows() {
    return new HoursRowParagraphField({
      machineName: 'field_hours_rows',
      parentElement: this,
    });
  }

  get hoursRowsHeaderWhenClosed() {
    return new Field({
      machineName: 'field_hours_rows_header_closed',
      parentElement: this,
    });
  }

  get hoursRowsHeaderWhenOpen() {
    return new Field({
      machineName: 'field_hours_rows_header_open',
      parentElement: this,
    });
  }

  get showHoursToggle() {
    return new Field({
      machineName: 'field_show_hours_toggle',
      parentElement: this,
    });
  }

  async initialize() {
    await this.#updateHours();

    if (this.hideHoursToggle.isRendered) {
      this.#updateHideHoursToggle();
    }

    if (this.showHoursToggle.isRendered) {
      this.#updateShowHoursToggle();
    }
  }

  async #updateHours(max = 3) {
    const showField = field => field.$().css('visibility', 'visible');

    const hoursOfOperation = await this.#luminisServiceUtils.getVirtualUrgentCareHoursOfOperation();

    if (hoursOfOperation?.length) {
      showField(this.hoursRowsHeaderWhenClosed);
      showField(this.hoursRowsHeaderWhenOpen);
      showField(this.showHoursToggle);

      for (let i = 0; i < hoursOfOperation.length; i++) {
        if (i + 1 > max) break;

        const { instantOpen, instantClosed } = hoursOfOperation[i];
        const instantOpenJsDate = new Date(instantOpen);
        const instantClosedJsDate = new Date(instantClosed);

        if (i === 0) {
          this.#updateHoursRowsHeaderWhenOpen(instantClosedJsDate);
        }

        const dayDisplay = DateDisplayUtils.weekdayWithDate(
          instantOpenJsDate,
        );
        const hoursDisplay = DateDisplayUtils.hoursRange(
          instantOpenJsDate,
          instantClosedJsDate,
        );

        this.#addHoursRow(i);
        this.#updateHoursRow(i, dayDisplay, hoursDisplay);
      }

      showField(this.hoursRows);
    }
  }

  #updateHoursRowsHeaderWhenOpen(instantClosedJsDate) {
    const $hoursRowsHeaderWhenOpenFieldItem = this.hoursRowsHeaderWhenOpen.item.$();
    if ($hoursRowsHeaderWhenOpenFieldItem.length) {
      const text = $hoursRowsHeaderWhenOpenFieldItem.text();
      const timeDisplay = DateDisplayUtils.timeWithOffset(
        instantClosedJsDate,
      );

      $hoursRowsHeaderWhenOpenFieldItem.text(`${text} ${timeDisplay}`);
    }
  }

  #addHoursRow(index) {
    const $hoursRowInstance = this.hoursRows.item.$(index);
    if ($hoursRowInstance.length) {
      // This hours row instance already exists
    } else {
      const $previousHoursRowInstance = this.hoursRows.item.$(index - 1);
      const $previousHoursRowInstanceClone = $previousHoursRowInstance.clone();
      $previousHoursRowInstance.after($previousHoursRowInstanceClone);
    }
  }

  #updateHoursRow(index, day, hours) {
    this.hoursRows.paragraph.day.item.$(index).text(day);
    this.hoursRows.paragraph.hours.item.$(index).text(hours);
  }

  #updateHideHoursToggle() {
    const $hideHoursToggle = this.hideHoursToggle.$();

    $hideHoursToggle.on(DOM_EVENTS.CLICK, () =>
      this.#toggleHoursRowDisplay(false),
    );

    $hideHoursToggle.on(DOM_EVENTS.KEYPRESS, event => {
      if (event.key === KEYS.ENTER) {
        this.#toggleHoursRowDisplay(false);
      }
    });
  }

  #updateShowHoursToggle() {
    const $showHoursToggle = this.showHoursToggle.$();

    $showHoursToggle.on(DOM_EVENTS.CLICK, () =>
      this.#toggleHoursRowDisplay(true),
    );

    $showHoursToggle.on(DOM_EVENTS.KEYPRESS, event => {
      if (event.key === KEYS.ENTER) {
        this.#toggleHoursRowDisplay(true);
      }
    });
  }

  #toggleHoursRowDisplay(show) {
    const showField = field => field.$().removeClass(CLASS_NAMES.HIDDEN);
    const hideField = field => field.$().addClass(CLASS_NAMES.HIDDEN);

    if (show) {
      hideField(this.showHoursToggle);
      showField(this.hideHoursToggle);
      showField(this.hoursRows);
    } else {
      showField(this.showHoursToggle);
      hideField(this.hideHoursToggle);
      hideField(this.hoursRows);
    }
  }
}
